import { WatchQueryFetchPolicy } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { advancedFilter, FilterField, keywordFilter } from '@neo.tax/shared';
import {
  GET_SRED_PROJECTS,
  GetSredProjectsQuery,
  GetSredProjectsQueryVariables,
  SredProjectFilter,
  SredProjectSort,
} from '@neo.tax/shared/graphql';
import { useCursorPaginatedQuery } from '@neo.tax/shared/hooks/useCursorPaginatedQuery';
import { useSticky } from '@neo.tax/shared/hooks/useSticky';
import { Conditions } from '@neo.tax/shared/organisms/filter-popover';

import { useSredContext } from '../context';

const GET_SRED_PROJECTS_KEY = 'GET_SRED_PROJECTS_KEY';

export type SredProject = NonNullable<GetSredProjectsQuery['getSredProjects']['edges']>[number]['node'];

interface UseSredProjectsProps {
  filterConditions?: Conditions<FilterField<SredProjectFilter>>;
  numElementsInitialFetch?: number;
  searchKeyword?: string | null;
  skip?: boolean;
  sorting?: Array<SredProjectSort> | SredProjectSort;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export const useSredProjects = ({
  fetchPolicy,
  filterConditions,
  numElementsInitialFetch,
  searchKeyword,
  skip = false,
  sorting,
}: UseSredProjectsProps = {}): {
  loading: boolean;
  sredProjects: SredProject[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const { sredTaxCreditId } = useSredContext();

  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetSredProjectsQuery, GetSredProjectsQueryVariables>(
    GET_SRED_PROJECTS,
    {
      variables: {
        sredTaxCreditId,
        filter: {
          and: [
            keywordFilter(['name', 'projectOwnerName'], searchKeyword),
            advancedFilter<SredProjectFilter>(filterConditions),
          ],
        },
        sorting: sorting ?? [],
      },
      context: {
        debounceKey: GET_SRED_PROJECTS_KEY,
      },
      fetchPolicy,
      skip,
    },
    {
      getConnection: (data) => data.getSredProjects,
      numElementsPerFetch: 40,
      numElementsInitialFetch,
    },
  );

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const sredProjects = stickyData?.getSredProjects?.edges.map((edge) => edge.node) ?? null;

  if (loading && !sredProjects) {
    return {
      loading: true,
      sredProjects: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore: false,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    throw createError('MissingSredProjects', 'No data returned from getSredProjects query');
  }

  return {
    loading,
    sredProjects,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
